'use client'

import {
    getOverdueAgendaEvents,
    resolveAgendaEvent,
} from '@/lib/api/agenda/agenda-api'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
    MdCancel,
    MdCheckCircle,
    MdClose,
    MdPersonOff,
    MdSchedule,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { getEventTypeLabels, isTaskType } from '../../utils/status-colors'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

interface Props {
    forceOpen?: boolean
    onForceOpenHandled?: () => void
    onEventResolved?: () => void
}

type ResolveStatus = 'COMPLETED' | 'NO_SHOW' | 'NO_SHOW_JUSTIFIED' | 'CANCELED'

export function OverdueEventsModal({ forceOpen, onForceOpenHandled, onEventResolved }: Props) {
    const { user } = useAuth()
    const { socket } = useContext(SocketContext)
    const [isOpen, setIsOpen] = useState(false)
    const [events, setEvents] = useState<AgendaEvent[]>([])
    const [loading, setLoading] = useState(false)
    const [resolvingId, setResolvingId] = useState<number | null>(null)

    const loadOverdueEvents = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getOverdueAgendaEvents()
            if (response.success && response.result) {
                setEvents(response.result)
                if (response.result.length > 0) {
                    setIsOpen(true)
                }
            }
        } catch {
            // Silencioso - não bloquear a UI
        } finally {
            setLoading(false)
        }
    }, [])

    // Carregar ao montar
    useEffect(() => {
        loadOverdueEvents()
    }, [loadOverdueEvents])

    // Forçar abertura quando vindo de notificação
    useEffect(() => {
        if (forceOpen) {
            loadOverdueEvents()
            onForceOpenHandled?.()
        }
    }, [forceOpen])

    // Escutar WebSocket para novos eventos atrasados
    useEffect(() => {
        if (!socket || !user?.company?.id) return

        const eventName = `overdue-agenda-events-${user.company.id}`

        const handler = (data: { events: any[]; count: number }) => {
            if (data.count > 0) {
                loadOverdueEvents()
            }
        }

        socket.on(eventName, handler)

        return () => {
            socket.off(eventName, handler)
        }
    }, [socket, user?.company?.id, loadOverdueEvents])

    const handleResolve = async (eventId: number, status: ResolveStatus) => {
        setResolvingId(eventId)
        try {
            const response = await resolveAgendaEvent(eventId, status)
            if (response.success) {
                const statusLabels: Record<ResolveStatus, string> = {
                    COMPLETED: 'concluído',
                    NO_SHOW: 'marcado como falta',
                    NO_SHOW_JUSTIFIED: 'marcado como falta justificada',
                    CANCELED: 'cancelado',
                }
                toast.success(`Agendamento ${statusLabels[status]}`)
                setEvents((prev) => prev.filter((e) => e.id !== eventId))
                onEventResolved?.()
            } else {
                toast.error(response.error || 'Erro ao resolver agendamento')
            }
        } catch {
            toast.error('Erro ao resolver agendamento')
        } finally {
            setResolvingId(null)
        }
    }

    // Fechar se não houver mais eventos
    useEffect(() => {
        if (events.length === 0 && isOpen) {
            setIsOpen(false)
        }
    }, [events.length, isOpen])

    if (!isOpen || events.length === 0) return null

    return (
        <dialog className="modal modal-open z-[60]">
            <div className="modal-box !max-w-2xl max-h-[80vh]">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-warning/20">
                            <MdWarning className="text-2xl text-warning" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">
                                Agendamentos Atrasados
                            </h3>
                            <p className="text-sm text-base-content/60">
                                {events.length} agendamento{events.length > 1 ? 's' : ''} pendente{events.length > 1 ? 's' : ''} passaram do horário
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={() => setIsOpen(false)}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                {/* Lista de eventos */}
                <div className="flex flex-col gap-3 overflow-y-auto max-h-[55vh] pr-1">
                    {events.map((event) => (
                        <OverdueEventCard
                            key={event.id}
                            event={event}
                            onResolve={handleResolve}
                            resolving={resolvingId === event.id}
                        />
                    ))}
                </div>

                {/* Footer */}
                <div className="modal-action">
                    <button
                        className="btn btn-ghost"
                        onClick={() => setIsOpen(false)}
                    >
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop bg-black/40" onClick={() => setIsOpen(false)} />
        </dialog>
    )
}

interface OverdueEventCardProps {
    event: AgendaEvent
    onResolve: (id: number, status: ResolveStatus) => void
    resolving: boolean
}

function OverdueEventCard({ event, onResolve, resolving }: OverdueEventCardProps) {
    const startDate = new Date(event.startDate)
    const endDate = new Date(event.endDate)

    const formattedDate = format(startDate, "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
    })
    const formattedEndTime = format(endDate, 'HH:mm', { locale: ptBR })

    const serviceLabels = useServiceLabel()
    const eventTypeLabels = getEventTypeLabels(serviceLabels)
    const typeName = event.type
        ? eventTypeLabels[event.type] || event.type
        : 'Evento'

    const linkedEntity = event.consultationId
        ? serviceLabels.Consultation
        : event.examId
            ? serviceLabels.Exam
            : event.orderId
                ? serviceLabels.Order
                : event.teleconsultationRoom
                    ? serviceLabels.Teleconsultation
                    : null

    return (
        <div className="bg-base-200 rounded-xl p-4 border border-orange-300/30">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <MdSchedule className="text-orange-500 shrink-0" />
                        <span className="font-semibold truncate">
                            {event.title}
                        </span>
                        <span className="badge badge-warning badge-xs shrink-0">
                            {typeName}
                        </span>
                    </div>

                    <div className="text-sm text-base-content/70 space-y-0.5">
                        {event.client?.name && (
                            <p>Paciente: <span className="font-medium">{event.client.name}</span></p>
                        )}
                        {event.agenda?.professional?.name && (
                            <p>Profissional: <span className="font-medium">{event.agenda.professional.name}</span></p>
                        )}
                        <p>
                            {formattedDate} - {formattedEndTime}
                        </p>
                        {linkedEntity && (
                            <p className="text-xs text-base-content/50">
                                Vinculado a: {linkedEntity}
                                {event.consultationId && ` #${event.consultationId}`}
                                {event.examId && ` #${event.examId}`}
                                {event.orderId && ` #${event.orderId}`}
                                {event.teleconsultationRoom && ` #${event.teleconsultationRoom.id}`}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Ações */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-base-300/50">
                <button
                    className="btn btn-xs btn-success gap-1"
                    onClick={() => onResolve(event.id, 'COMPLETED')}
                    disabled={resolving}
                >
                    {resolving ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdCheckCircle className="text-sm" />
                    )}
                    Concluído
                </button>
                {/* Tarefa não tem falta: ou foi feita, ou foi cancelada. */}
                {!isTaskType(event.type) && (
                    <>
                        <button
                            className="btn btn-xs btn-ghost gap-1"
                            onClick={() => onResolve(event.id, 'NO_SHOW')}
                            disabled={resolving}
                        >
                            <MdPersonOff className="text-sm" />
                            Não compareceu
                        </button>
                        <button
                            className="btn btn-xs btn-ghost gap-1"
                            onClick={() => onResolve(event.id, 'NO_SHOW_JUSTIFIED')}
                            disabled={resolving}
                        >
                            <MdPersonOff className="text-sm" />
                            Falta justificada
                        </button>
                    </>
                )}
                <button
                    className="btn btn-xs btn-ghost text-error gap-1"
                    onClick={() => onResolve(event.id, 'CANCELED')}
                    disabled={resolving}
                >
                    <MdCancel className="text-sm" />
                    Cancelar
                </button>
            </div>
        </div>
    )
}
